import { StockItem, StockItemService } from './StockItem.service';
import { AcumaticaRequestOptions, AcumaticaService } from '../acumatica.service';
import { AcumaticaEntity, AcumaticaEntityService } from './acumatica-entity';
import { Injectable } from '@angular/core';
import * as errors from '../acumatica-errors';
import { Observable } from 'rxjs';
import { Acumatica, AcumaticaFieldFormat } from '../acumatica-field-decorator';
import { Address, DocContact } from './Contacts.service';

@Injectable({
    providedIn: 'root'
})
export class SalesOrderService extends AcumaticaEntityService {
    constructor(
        acumatica: AcumaticaService,
    ) {
        super(acumatica, 'SalesOrder', SalesOrder);
    }

    disableStockItemRetrieval: boolean = false;

    get(OrderType: string, OrderNbr: string, options: AcumaticaRequestOptions = {}, getStockItems: boolean = true): Observable<SalesOrder> {
        options.noCache = true; // never cache Sales Orders

        return new Observable<SalesOrder>(subscriber => {
            this.disableStockItemRetrieval = !getStockItems;
            super.getEntity([OrderType, OrderNbr], {
                ...{ loader: "Getting Sales Order" },
                ...options
            }).subscribe(so => {
                this.disableStockItemRetrieval = false;
                subscriber.next(so);
                subscriber.complete();
            });
        })
    }

    put(salesOrder: SalesOrder, options: AcumaticaRequestOptions = {}): Observable<SalesOrder> {
        return new Observable<SalesOrder>(subscriber => {
            super.putEntity(salesOrder, options).subscribe(
                salesOrder => {
                    subscriber.next(salesOrder)
                },
                error => {
                    subscriber.error(error);
                });
        });
    }

    addShippedItem(OrderType: string, OrderNbr: string, InventoryID: string, WarehouseID: string, Qty: number, IsFree: boolean, LineNote: string | null, message: string | null = "Adding Item to Sales Order")
        : Observable<SalesOrder> {
        let loaderOptions = message === null
            ? null
            : { message: message }
        return new Observable<SalesOrder>(subscriber => {
            this.acumatica.action('SalesOrder', 'AddShippedItem', {
                OrderType: OrderType,
                OrderNbr: OrderNbr
            }, {
                InventoryID: InventoryID,
                WarehouseID: WarehouseID,
                Qty: Qty,
                IsFree: IsFree,
                LineNote: LineNote,
                APIInvoked: true,
            }, loaderOptions).subscribe(subscriber);
        });
    }
}

export class SalesOrderDetail extends AcumaticaEntity {
    constructor(
        init?: Partial<SalesOrderDetail>,
        private acumatica?: AcumaticaService,
    ) {
        super();
        if (init) Object.assign(this, init);
    }

    _fetchInventoryItem: boolean = true;

    @Acumatica('InventoryID')
    get InventoryID() { return this._InventoryID; }
    set InventoryID(value) {
        this._InventoryID = value;

        if (this._fetchInventoryItem) {
            // object may be instantiated through deserialization Acumatica JSON response and will not receive service injection
            const stockItems = this.acumatica?.injector.get(StockItemService);
            const salesOrderService = this.acumatica?.injector.get(SalesOrderService);

            // Get Stock Item
            if (stockItems && !salesOrderService?.disableStockItemRetrieval && value != 'M') {
                stockItems
                    .get(value!, { loader: null }, false)
                    .subscribe(item => { this.item = item });
            }
        }
    }
    private _InventoryID!: string;
    item!: StockItem;

    @Acumatica({ write: false }) LineDescription: string | null = null;
    @Acumatica() WarehouseID: string | null = null;
    @Acumatica() OrderQty: number = 0;
    @Acumatica() QtyOnShipments: number = 0;
    @Acumatica() FreeItem: boolean = false;
    @Acumatica() Completed: boolean = false;
    @Acumatica() LineNbr!: number;
    @Acumatica() note?: string | null = undefined;
    @Acumatica() ProjectName: string | null = null;
    @Acumatica() ProjectLocation: string | null = null;
}

export class SalesOrderShipment extends AcumaticaEntity {
    @Acumatica() ShipmentType!: string;
    @Acumatica() ShipmentNbr!: string;
    @Acumatica() ShipmentDate: Date | null = null;
    @Acumatica() ShippedQty: number | null = null;
    @Acumatica() Status: string | null = null;
}

export class SalesOrder extends AcumaticaEntity {
    @Acumatica() note!: string;
    @Acumatica() OrderType!: string;
    @Acumatica() OrderNbr!: string;
    @Acumatica() CustomerID: string | null = null;
    @Acumatica() DestinationWarehouseID?: string | null;
    @Acumatica() PreferredWarehouseID?: string | null;
    @Acumatica() LocationID!: string;
    @Acumatica() Status!: string;
    @Acumatica() Description: string | null = null;
    @Acumatica({ format: AcumaticaFieldFormat.Date }) Date!: Date;
    @Acumatica({ name: "SchedShipment", format: AcumaticaFieldFormat.Date }) ShipmentDate!: Date;
    @Acumatica() ShipVia: string | null = null;
    @Acumatica() OrderedBy?: string | null;
    @Acumatica() EmailAddress?: string | null;
    @Acumatica() Phone?: string | null;
    @Acumatica() MarketplaceID?: string | null;
    @Acumatica() OrderedbyRep?: string | null;
    @Acumatica('CustomerOrder') CustomerOrderNbr?: string | null;
    @Acumatica({ writer: (value) => value || undefined }) ShipperUPSFedExNbr?: string;

    @Acumatica({ writer: (value) => value ? true : undefined }) ShipToContactOverride?: boolean;
    @Acumatica({ writer: (value) => value ? true : undefined }) ShipToAddressOverride?: boolean;
    @Acumatica(DocContact) ShipToContact?: DocContact;
    @Acumatica(Address) ShipToAddress?: Address;

    @Acumatica(SalesOrderDetail) Details?: SalesOrderDetail[];
    @Acumatica(SalesOrderShipment) Shipments?: SalesOrderShipment[];

    get OrderKey(): string { return this.OrderType + '/' + this.OrderNbr; }

    constructor(init?: Partial<SalesOrder>) {
        super();

        if (init) Object.assign(this, init);
    }
}
